/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import { ReceiptHeadersService } from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { ReceiptHeaderStore } from './receipt-header.store';
import { ReceiptLineStore } from '../receipt-lines/receipt-line.store';
import {
    ReceiptHeaderCreate,
    ReceiptHeaderRead,
    ReceiptHeaderSearchParams,
} from '../../entities/receipt-header.dto';
import { Operation } from 'fast-json-patch';
import {
    ReceiptLineDto,
    ReceiptLineRead,
} from '../../entities/receipt-line.dto';
import { asArraySafe } from '@pf/shared-utility';
import { ReceiptLineMapper } from '../receipt-lines/receipt-line.mapper';

@Injectable()
export class ReceiptHeaderDataService extends AbstractDataService<
    ReceiptHeaderRead,
    ReceiptHeaderCreate
> {
    private readonly receiptLineStore = inject(ReceiptLineStore);
    private readonly receiptLineMapper = inject(ReceiptLineMapper);
    protected EntityName = PFWmsEntities.ReceiptHeader;
    constructor(
        private service: ReceiptHeadersService,
        store: ReceiptHeaderStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(
        body: ReceiptHeaderCreate
    ): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderCreate({
            receiptHeaderViewModelCreateBody: body,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected deleteObs$(id: string): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderDelete({
            id,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected undoDeleteObs$(id: string): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderUndoDelete({
            id,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected getObs$(id: string): Observable<ReceiptHeaderRead> {
        return this.service
            .receiptHeaderReadById({
                id,
            })
            .pipe(
                map(header => {
                    this.addLinesToStore(header as ReceiptHeaderRead);
                    return header;
                })
            ) as Observable<ReceiptHeaderRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ReceiptHeaderRead> {
        return this.service.receiptHeaderUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ReceiptHeaderRead>;
    }
    protected searchObs$(
        searchParams: ReceiptHeaderSearchParams
    ): Observable<IPagedResult<ReceiptHeaderRead>> {
        return this.service.receiptHeaderSearch(searchParams).pipe(
            map(result => result as IPagedResult<ReceiptHeaderRead>),
            tap(result => {
                asArraySafe(result.data).forEach(header => {
                    this.addLinesToStore(header);
                });
            })
        );
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ReceiptHeaderRead>>> {
        return this.service.receiptHeaderGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ReceiptHeaderRead>>
        >;
    }

    private addLinesToStore(header: ReceiptHeaderRead) {
        asArraySafe(header.receiptLines).forEach(line => {
            this.receiptLineStore.add(line as ReceiptLineRead, false);
        });
    }

    receiveLines$(
        headerId: string,
        lineIds: string[],
        receivingContainerId: string
    ) {
        return this.service
            .receiptHeaderReceiveMultiple({
                receiptHeaderId: headerId,
                receiptHeaderReceiveLinesViewModel: {
                    inventoryReceiptLineIds: lineIds,
                    containerId: receivingContainerId,
                },
            })
            .pipe(
                map(receivedLines => {
                    if (!receivedLines.length) {
                        return [];
                    }
                    this.receiptLineStore.upsertMany(
                        receivedLines as ReceiptLineRead[]
                    );
                    return this.receiptLineMapper.toDTOList(
                        receivedLines as ReceiptLineRead[]
                    );
                })
            ) as Observable<ReceiptLineDto[]>;
    }
}
